import { createContext, useState, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import type { DocumentTemplate, DocumentField } from "../types";
import { mapApiTemplateToLocal } from "../types";
import type { ApiNode, NodeData } from "@/workflow_api/types/workflow";
import type { NodeVariable } from "../../types";
import {
  getSerializedDocumentGeneratorNode,
  getDeserializedDocumentGeneratorNode,
} from "../serialization";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useGetNode } from "@/workflow_api/services/nodes.api";
import { useGetDocumentTemplatesAsInifinite } from "@/queries/document-templates/document-templates.api";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";

export interface VariableFileName {
  base: string;
  include_request_id: boolean;
  include_date: boolean;
}
interface DocumentGeneratorContextType {
  selectedTemplate: DocumentTemplate | null;
  setSelectedTemplate: (template: DocumentTemplate | null) => void;
  fileName: string;
  setFileName: (fileName: string) => void;
  fields: DocumentField[];
  setFields: (fields: DocumentField[]) => void;
  variableName: VariableFileName;
  setVariableName: (variableName: VariableFileName) => void;
  isVariableNameValid: boolean;
  variableNameError: string | null;
  templates: DocumentTemplate[];
  isLoadingTemplates: boolean;
  templatesError: string | null;
  reset: () => void;
  serialize: (
    currentNode: ApiNode,
    title: string,
    flowId: string,
  ) => { node_id: string; node: ApiNode };
  deserialize: (node: ApiNode, inputVariables: NodeVariable[]) => Promise<void>;
  saveNode: (title: string, manual_continue?: boolean) => Promise<void>;
  currentNode: ApiNode | null;
  setCurrentNode: (node: ApiNode | null) => void;
  onTemplateSearch: (search: string) => void;
  templateSearchQuery: string;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

const DocumentGeneratorContext = createContext<
  DocumentGeneratorContextType | undefined
>(undefined);

export { DocumentGeneratorContext };

interface DocumentGeneratorProviderProps {
  children: ReactNode;
}

export const DocumentGeneratorProvider = ({
  children,
}: DocumentGeneratorProviderProps) => {
  const { flowId } = useWorkflowStore();
  const { variables: inputVariables } = useGetVariables();
  const { selectedNodeId, nodeDetailsOpen } = useUIStore();
  const { mutateAsync: getNode, isPending: isLoadingNode } = useGetNode();
  const { mutateAsync: updateNode } = useUpdateNode(flowId);
  const [templateSearch, setTemplateSearch] = useState("");

  // Obtener templates desde el API
  const {
    data: templatesResponse,
    isLoading: isLoadingTemplates,
    error: templatesApiError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetDocumentTemplatesAsInifinite({
    relationships: "variables", // Incluir variables para tener la info completa
    per_page: 100, // Obtener muchos templates para evitar paginación
    "filter[status]": "active",
    ...(templateSearch ? { "filter[search]": templateSearch } : {}), // Agregar filtro de búsqueda solo si hay algo escrito
  });

  // Convertir el error a string para el contexto
  const templatesError = templatesApiError
    ? (templatesApiError as Error).message || "Error al cargar plantillas"
    : null;

  const [selectedTemplate, setSelectedTemplate] =
    useState<DocumentTemplate | null>(null);
  const [fileName, setFileName] = useState("");
  const [currentNode, setCurrentNode] = useState<ApiNode | null>(null);
  const [fields, setFields] = useState<DocumentField[]>([
    {
      name: "nombre_prueba",
      label: "nombre_prueba",
      type: "text",
      required: true,
      value: "",
      placeholder: "Escribe un texto",
      inputType: "input",
    },
    {
      name: "Precio_prueba",
      label: "Precio_prueba",
      type: "number",
      required: true,
      value: "",
      placeholder: "Escribe un número",
      inputType: "input",
    },
    {
      name: "activo",
      label: "activo",
      type: "boolean",
      required: true,
      value: "false",
      placeholder: "False",
      inputType: "input",
    },
    {
      name: "fecha_prueba",
      label: "fecha_prueba",
      type: "date",
      required: false,
      value: "",
      placeholder: "Selecciona una fecha",
      inputType: "input",
    },
  ]);
  const [variableName, setVariableName] = useState<VariableFileName>({
    base: "",
    include_request_id: false,
    include_date: false,
  });

  const getTemplatesWithVariables = (data: DocumentTemplate[]) => {
    return data.filter((template) => (template?.variables?.length ?? 0) > 0); //TODO Remover en caso de que puedan existir plantillas sin variables
  };

  const templates = useMemo(() => {
    if (!templatesResponse) return [];
    const allResults = templatesResponse.pages.flatMap((page) => page.data);
    return getTemplatesWithVariables(allResults.map(mapApiTemplateToLocal));
  }, [templatesResponse]);

  // Función helper para mapear tipos del API a tipos de campo
  const mapApiTypeToFieldType = (
    apiType: string,
  ): "text" | "number" | "date" | "boolean" | "variable" => {
    switch (apiType.toUpperCase()) {
      case "STRING":
        return "text";
      case "INTEGER":
      case "NUMBER":
      case "FLOAT":
        return "number";
      case "DATE":
        return "date";
      case "BOOLEAN":
        return "boolean";
      default:
        return "text";
    }
  };

  // Función helper para obtener placeholder según el tipo
  const getPlaceholderForFieldType = (type: string): string => {
    switch (type) {
      case "text":
        return "Escribe un texto";
      case "number":
        return "Escribe un número";
      case "date":
        return "Selecciona una fecha";
      case "boolean":
        return "Elige verdadero o falso";
      default:
        return "Ingresa un valor";
    }
  };

  const handleTemplateSelection = (template: DocumentTemplate | null) => {
    setSelectedTemplate(template);
    // Solo cambiar el nombre del archivo basado en la plantilla
    if (template) {
      setFileName(`${template.name.toLowerCase().replace(/\s+/g, "_")}`);

      // Si la plantilla tiene variables del API, crear los campos basados en esas variables
      if (template.variables && template.variables.length > 0) {
        const templateFields: DocumentField[] = template.variables.map(
          (variable) => {
            // Mapear el tipo del API al tipo de campo
            const fieldType = mapApiTypeToFieldType(variable.type || "STRING");
            return {
              name: variable.mapping_name,
              label: variable.display_name,
              type: fieldType,
              required: false, // Se puede ajustar según la lógica de negocio
              value: "",
              placeholder: getPlaceholderForFieldType(fieldType),
              inputType: "input",
            };
          },
        );
        setFields(templateFields);
      } else {
        // Mantener los campos hardcodeados como fallback
        setFields([
          {
            name: "nombre_prueba",
            label: "nombre_prueba",
            type: "text",
            required: true,
            value: "",
            placeholder: "Escribe un texto",
            inputType: "input",
          },
          {
            name: "Precio_prueba",
            label: "Precio_prueba",
            type: "number",
            required: true,
            value: "",
            placeholder: "Escribe un número",
            inputType: "input",
          },
          {
            name: "activo",
            label: "activo",
            type: "boolean",
            required: true,
            value: "false",
            placeholder: "False",
            inputType: "input",
          },
          {
            name: "fecha_prueba",
            label: "fecha_prueba",
            type: "date",
            required: false,
            value: "",
            placeholder: "Selecciona una fecha",
            inputType: "input",
          },
        ]);
      }
    }
  };

  /**
   * Fetch node details when node is selected
   */
  useEffect(() => {
    if (!selectedNodeId || !nodeDetailsOpen) return;

    const run = async () => {
      try {
        const node = await getNode(selectedNodeId);
        if (node) {
          setCurrentNode(node);
          // Deserialize node data if it exists
          if (node.inputs && node.inputs.length > 0) {
            await deserialize(node, inputVariables);
          }
        }
      } catch (error) {
        console.error("[DocumentGenerator] getNode error:", error);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, nodeDetailsOpen, inputVariables, getNode]);

  const isVariableNameValid = variableName.base?.trim().length > 0;
  const variableNameError = !isVariableNameValid
    ? "El nombre de la variable es requerido y debe contener al menos una letra"
    : null;

  const reset = () => {
    setSelectedTemplate(null);
    setFileName("");
    setFields([]);
    setVariableName({
      base: "",
      include_request_id: false,
      include_date: false,
    });
  };

  const serialize = (currentNode: ApiNode, title: string, flowId: string) => {
    if (!selectedTemplate) {
      throw new Error("No template selected");
    }

    return getSerializedDocumentGeneratorNode(
      currentNode,
      title,
      flowId,
      selectedTemplate.id,
      selectedTemplate.name,
      //fileName,
      fields,
      variableName,
      inputVariables,
      selectedTemplate, // Pass the selectedTemplate to access mapping_name
    );
  };

  const deserialize = async (node: ApiNode, inputVariables: NodeVariable[]) => {
    const deserializedData = getDeserializedDocumentGeneratorNode(
      node as unknown as NodeData,
      inputVariables,
      templates, // Pasar las plantillas del API
    );

    // La función ya maneja la búsqueda en plantillas del API
    setSelectedTemplate(deserializedData.selectedTemplate);
    setFileName(deserializedData.fileName);
    setFields(deserializedData.fields);
    setVariableName(deserializedData.variableName);

    // Esperar un poco para que React termine de renderizar los componentes con las variables
    // Esto asegura que las variables estén visualmente asignadas antes de terminar el loading
    await new Promise((resolve) => setTimeout(resolve, 200));
  };

  /**
   * Serialize and save the node
   * @param title
   * @param manual_continue
   */
  const saveNode = async (title: string, manual_continue?: boolean) => {
    if (!selectedTemplate || !currentNode) {
      throw new Error("Error al guardar el nodo");
    }

    await updateNode(
      getSerializedDocumentGeneratorNode(
        currentNode,
        title,
        flowId,
        selectedTemplate.id,
        selectedTemplate.name,
        //fileName,
        fields,
        variableName,
        inputVariables,
        selectedTemplate, // Pass the selectedTemplate to access mapping_name
        manual_continue,
      ),
    );
  };

  const value: DocumentGeneratorContextType = {
    selectedTemplate,
    setSelectedTemplate: handleTemplateSelection,
    fileName,
    setFileName,
    fields,
    setFields,
    variableName,
    setVariableName,
    isVariableNameValid,
    variableNameError,
    templates,
    isLoadingTemplates: isLoadingTemplates || isLoadingNode,
    templatesError, // Agregar esta línea que faltaba
    reset,
    serialize,
    deserialize,
    saveNode,
    currentNode,
    setCurrentNode,
    onTemplateSearch: setTemplateSearch,
    templateSearchQuery: templateSearch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };

  return (
    <DocumentGeneratorContext.Provider value={value}>
      {children}
    </DocumentGeneratorContext.Provider>
  );
};
